import React, {
    createContext,
    useContext,
    useEffect,
    useRef,
    useState
} from 'react';
import { DrawerContainer, DrawerWrapper } from './HBSideDrawer.styles';
import { useSideDrawerState, DrawerState } from './useSideDrawerState';

// Re-export DrawerState for consumers
export type { DrawerState };

interface IDrawerContextType {
    isPinned: boolean;
    isExpanded: boolean;
    isMinimized: boolean;
    isHovered: boolean;
    drawerState: DrawerState;
    togglePinned: (options?: { forceClose?: boolean }) => void;
}

if (!globalThis.DrawerContext) {
    globalThis.DrawerContext = createContext<IDrawerContextType | undefined>(
        undefined
    );
}

const DrawerContext =
    globalThis.DrawerContext as React.Context<IDrawerContextType>;

export const useSideDrawer = () => {
    const context = useContext(DrawerContext);
    if (!context) {
        throw new Error('useDrawer must be used within a Drawer');
    }
    return context;
};

export interface IHBSideDrawer {
    children: React.ReactNode;
    open?: boolean;
    /** Width in pixels when the drawer is collapsed (not hovered or pinned). */
    collapsedWidth: number;
    /** Width in pixels when the drawer is expanded (hovered or pinned open). */
    expandedWidth: number;
    /** When true, the drawer starts in the pinned-open (expanded) state. */
    initialPinned?: boolean;
    /** Called when the drawer finishes expanding or collapsing, with the new open state. */
    onOpenChange?: (isOpen: boolean) => void;
    /** Called when the pin state changes; forceClose is true when the drawer was unpinned and force-collapsed in one action. */
    onPinChange?: ({
        isPinned,
        forceClose
    }: {
        isPinned: boolean;
        forceClose?: boolean;
    }) => void;
}

function HBSideDrawer({
    children,
    open,
    collapsedWidth,
    expandedWidth,
    initialPinned,
    onOpenChange,
    onPinChange
}: IHBSideDrawer) {
    const [isPinned, setIsPinned] = useState(initialPinned);
    const [isHovered, setIsHovered] = useState(false);
    const [isSkipHover, setIsSkipHover] = useState(false);

    // originally, the effect in this component used eslint-disable to remove onOpenChange
    // from effect diff list. It seems users of this component used that fact to pass
    // non-memoized functions to this prop. To perseve backward compatibility
    // I'm using a ref for it
    const onOpenChangeRef = useRef(onOpenChange);
    onOpenChangeRef.current = onOpenChange;

    // Use the new state management hook
    const { drawerState, drawerRef } = useSideDrawerState({
        collapsedWidth,
        expandedWidth,
        initialPinned
    });

    // Maintain backward compatibility with isExpanded and isMinimized
    const isExpanded =
        drawerState === 'expanded' || drawerState === 'expanding';
    const isMinimized =
        drawerState === 'collapsed' || drawerState === 'collapsing';

    useEffect(() => {
        if (isExpanded) {
            onOpenChangeRef.current?.(isExpanded);
        }
        if (isMinimized) {
            onOpenChangeRef.current?.(!isMinimized);
        }
    }, [isExpanded, isMinimized]);

    const handleForceClose = () => {
        setIsPinned(false);
        setIsSkipHover(true);
        setTimeout(() => {
            setIsSkipHover(false);
        }, 400);
        onPinChange?.({ isPinned: false, forceClose: true });
    };

    const togglePinnedState = () => {
        const newPinnedState = !isPinned;
        setIsPinned(newPinnedState);
        onPinChange?.({ isPinned: newPinnedState, forceClose: false });
    };

    const togglePinned = (options?: { forceClose?: boolean }): void => {
        const { forceClose = false } = options || {};

        if (forceClose) {
            handleForceClose();
        } else {
            togglePinnedState();
        }
    };

    const handleMouseEnter = () => {
        setIsHovered(true);
    };

    const handleMouseLeave = () => {
        setIsHovered(false);
    };

    // todo: usememo?
    const contextValue: IDrawerContextType = {
        isPinned,
        isExpanded,
        isMinimized,
        isHovered,
        drawerState,
        togglePinned
    };

    return (
        // todo: implement radix dialog?
        <DrawerContext.Provider value={contextValue}>
            <DrawerContainer $collapsedWidth={collapsedWidth}>
                <DrawerWrapper
                    ref={drawerRef}
                    collapsedWidth={collapsedWidth}
                    expandedWidth={expandedWidth}
                    isPinned={isPinned}
                    isExpanded={isExpanded}
                    isSkipHover={isSkipHover}
                    open={open}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    data-testid="side-drawer-wrapper"
                >
                    {children}
                </DrawerWrapper>
            </DrawerContainer>
        </DrawerContext.Provider>
    );
}

export default HBSideDrawer;
